import {
  Button,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useShowSnackbar } from 'components/main/SnackbarProvider';
import States from 'constants/states';
import { Field, FieldProps, Form, Formik } from 'formik';
import React, { FC } from 'react';
import { useHistory } from 'react-router-dom';
import { createAccount } from 'services/AccountService';
import * as yup from 'yup';

const AccountFormSchema = yup
  .object()
  .shape({
    name: yup.string().required('Please enter a first and last name.'),
    email: yup
      .string()
      .email('Please enter a valid email address.')
      .required('Please enter an email address.'),
    twitter: yup.string(),
    school: yup
      .object()
      .shape({
        name: yup.string(),
        district: yup.string(),
        zip: yup
          .number()
          .typeError('Please enter a valid 5-digit numeric zip code.')
          .test(
            'length',
            'Please enter a valid 5-digit zip code.',
            (value: number) => !value || value.toString().length === 5,
          ),
        state: yup.string(),
      })
      .defined(),
    displayInfo: yup.boolean().required('Please make a selection.'),
  })
  .defined();

export type AccountForm = yup.InferType<typeof AccountFormSchema>;

const EMPTY_FORM = {
  name: '',
  email: '',
  twitter: '',
  school: {
    name: '',
    district: '',
    zip: undefined,
    state: '',
  },
  displayInfo: false,
};

interface Props {
  user: firebase.User;
  email: string;
  name: string;
}

const AccountSignupForm: FC<Props> = ({ user, email, name }) => {
  const history = useHistory();
  const showSnackbar = useShowSnackbar();

  return (
    <Formik<AccountForm>
      initialValues={{ ...EMPTY_FORM, name, email }}
      validationSchema={AccountFormSchema}
      onSubmit={async (values, { setSubmitting }) => {
        const success = await createAccount(values, user);
        setSubmitting(false);
        if (success) {
          history.push('/');
          showSnackbar(
            <Alert severity="success">
              You have successfully signed up. Please login to submit a lesson.
            </Alert>,
          );
        } else {
          showSnackbar(
            <Alert severity="error">
              An unknown error occurred creating user. Please contact an administrator.
            </Alert>,
          );
        }
      }}
    >
      <Form>
        <Grid direction="column" spacing={2} container>
          <Grid item>
            <Typography gutterBottom>Tell us about yourself:</Typography>
          </Grid>
          <Grid item>
            <Field name="name">
              {({ field, meta }: FieldProps<AccountForm['name']>) => (
                <TextField
                  id="name"
                  label="Your Name (First and Last)"
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                  helperText={meta.touched && meta.error}
                  error={meta.touched && Boolean(meta.error)}
                  {...field}
                />
              )}
            </Field>
          </Grid>
          <Grid item>
            <Field name="email">
              {({ field, meta }: FieldProps<AccountForm['email']>) => (
                <TextField
                  id="email"
                  label="Email Address"
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    readOnly: true,
                    disabled: true,
                  }}
                  helperText={meta.touched && meta.error}
                  error={meta.touched && Boolean(meta.error)}
                  {...field}
                />
              )}
            </Field>
          </Grid>
          <Grid item>
            <Field name="twitter">
              {({ field, meta }: FieldProps<AccountForm['twitter']>) => (
                <TextField
                  id="twitter"
                  label="Twitter Handle"
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                  helperText={meta.touched && meta.error}
                  error={meta.touched && Boolean(meta.error)}
                  {...field}
                />
              )}
            </Field>
          </Grid>
          <Grid item>
            <Field name="school.name">
              {({ field, meta }: FieldProps<AccountForm['school']['name']>) => (
                <TextField
                  id="schoolName"
                  label="School Name"
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                  helperText={meta.touched && meta.error}
                  error={meta.touched && Boolean(meta.error)}
                  {...field}
                />
              )}
            </Field>
          </Grid>
          <Grid item>
            <Field name="school.district">
              {({ field, meta }: FieldProps<AccountForm['school']['district']>) => (
                <TextField
                  id="schoolDistrict"
                  label="School District"
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                  helperText={meta.touched && meta.error}
                  error={meta.touched && Boolean(meta.error)}
                  {...field}
                />
              )}
            </Field>
          </Grid>
          <Grid container item spacing={2}>
            <Grid item xs={6}>
              <Field name="school.zip">
                {({ field, meta }: FieldProps<AccountForm['school']['zip']>) => (
                  <TextField
                    id="schoolZip"
                    label="School Zip Code"
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                    helperText={meta.touched && meta.error}
                    error={meta.touched && Boolean(meta.error)}
                    {...field}
                  />
                )}
              </Field>
            </Grid>
            <Grid item xs={6}>
              <Field name="school.state">
                {({ field, meta }: FieldProps<AccountForm['school']['state']>) => (
                  <FormControl error={meta.touched && Boolean(meta.error)} fullWidth>
                    <InputLabel shrink id="schoolStateLabel">
                      School State
                    </InputLabel>
                    <Select labelId="schoolStateLabel" id="schoolState" displayEmpty {...field}>
                      {States.map((state) => (
                        <MenuItem key={state.abbreviation} value={state.abbreviation}>
                          {state.name}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{meta.touched && meta.error}</FormHelperText>
                  </FormControl>
                )}
              </Field>
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <Field name="displayInfo">
            {({ field, meta, form }: FieldProps<AccountForm['displayInfo']>) => {
              const handleDisplayInfoChange = (value: boolean) => (_: React.ChangeEvent<{}>) => {
                form.setFieldValue('displayInfo', value);
              };

              return (
                <FormControl
                  error={meta.touched && Boolean(meta.error)}
                  component="fieldset"
                  required
                >
                  <FormLabel>
                    Would you like your name and school information to be displayed with your
                    lesson?
                  </FormLabel>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Radio
                          checked={field.value === true}
                          onChange={handleDisplayInfoChange(true)}
                        />
                      }
                      label="Yes, please display my name and school information."
                    />
                    <FormControlLabel
                      control={
                        <Radio
                          {...field}
                          checked={field.value === false}
                          onChange={handleDisplayInfoChange(false)}
                        />
                      }
                      label="No, I prefer for my submission to remain anonymous."
                    />
                  </FormGroup>
                  <FormHelperText>
                    {meta.touched && Boolean(meta.error)
                      ? meta.error
                      : "While we do not currently display each educator's name and school information along with lessons, we plan to add this feature in the future. Regardless of your answer, your email address and contact information will never be shared publicly."}
                  </FormHelperText>
                </FormControl>
              );
            }}
          </Field>
        </Grid>

        <Grid item>
          <Button type="submit" variant="contained" color="primary">
            Submit
          </Button>
        </Grid>
      </Form>
    </Formik>
  );
};

export default AccountSignupForm;
